import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Shield, HandHeart, Users, Info, Globe, Phone, Mail, AlertCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import useLang from '../hooks/useLang';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Heart,
  Shield,
  HandHeart,
  Users,
  Info,
  Globe
};

const IdeologiePage = () => {
  const { t, i18n } = useTranslation();
  const lang = useLang();
  const [values, setValues] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/faqs?lang=${lang}`)
      .then((r) => setFaqs(r.data?.faqs || []))
      .catch(() => setFaqs([]));
  }, [lang]);

  useEffect(() => {
    const fetchValues = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/values?lang=${lang}`);
        setValues(response.data.values || []);
      } catch (error) {
        console.error('Error fetching values:', error);
      }
    };
    fetchValues();
  }, [lang, i18n.language]);

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="ideologie-page">
      <SEO {...seoConfig.ideologie} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            {t('ideology.title')}
          </motion.h1>
          <p className="font-serif italic text-lg text-[#B9B2A8] mb-4">
            {t('ideology.subtitle')}
          </p>
          <p className="font-display text-2xl text-[#C9A84C] tracking-wider">
            {t('ideology.slogan')}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {values.map((value, index) => {
            const IconComponent = iconMap[value.icon] || Heart;
            return (
              <motion.div
                key={value.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 card-hover"
                data-testid={`value-card-${value.id}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#C9A84C]/20 flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-[#C9A84C]" />
                </div>
                <h3 className="font-display text-xl text-[#F5F0E8] mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-[#B9B2A8] leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Emergency Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#FF3B2F]/10 border border-[#FF3B2F]/30 rounded-2xl p-6 mb-16"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#FF3B2F] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg text-[#F5F0E8] mb-2">
                En cas de problème
              </h3>
              <p className="text-sm text-[#B9B2A8] mb-4">
                Si tu te sens mal, en danger, ou si tu es témoin d'un comportement inapproprié, 
                n'hésite pas à contacter immédiatement notre équipe sur place (t-shirt Invasion) 
                ou à nous joindre directement.
              </p>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="tel:+32478814497" 
                  className="inline-flex items-center gap-2 text-sm text-[#FF3B2F] hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  +32 478 81 44 97
                </a>
                <a 
                  href="mailto:info@invasionlatina.be" 
                  className="inline-flex items-center gap-2 text-sm text-[#FF3B2F] hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  info@invasionlatina.be
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-2xl text-[#F5F0E8] mb-6 text-center">
            Questions Fréquentes
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.length === 0 ? (
              <p className="text-center text-[#B9B2A8] py-4">
                Aucune question fréquente pour le moment.
              </p>
            ) : (
              faqs.map((faq, idx) => (
                <AccordionItem
                  key={faq.id || idx}
                  value={`item-${idx}`}
                  className="bg-white/5 border border-white/10 rounded-xl px-4"
                >
                  <AccordionTrigger className="text-[#F5F0E8] hover:text-[#C9A84C] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#B9B2A8]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))
            )}
          </Accordion>
        </motion.div>
      </div>
    </div>
  );
};

export default IdeologiePage;
